import React, { useContext } from 'react';
import { useLoader } from '../api/useLoader';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useParams } from 'react-router-dom';
import Spinner from '../components/spinner/spinner';
import { useIntl } from 'react-intl';
import translations from './userprofile.i18n';
import {
  HttpError,
  PraksisstedbrukerprofilKanRedigereDto,
} from '../api/generated/studiested-generated-api';
import PraksisstedUserprofile from './praksisstedUserprofile';
import PageLayout from '../pagelayout';
import { BackButton } from '../pagelayout/backButton';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import Activities from './activities.uha';
import Tilganger from './praksisstedbrukerTilganger';

const PraksisstedbrukerprofilPage: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { praksisstedApi } = useContext(StudiestedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);
  const { id } = useParams<{ id: string }>();

  const { data, reload } = useLoader(
    () =>
      praksisstedApi.praksisstedbrukerprofilIdGet({
        pathParams: { id: id as string },
      }),
    [id],
    true,
    true,
  );

  const canAdministerTilganger =
    data?.kanRedigere ===
    PraksisstedbrukerprofilKanRedigereDto.ProfilOgTilganger;

  const { formatMessage } = useIntl();

  const labels = {
    pageTitle: formatMessage({ id: translations.pageTitlePSUser.id }),
    title: formatMessage(
      { id: translations.titlePraksissted.id },
      {
        institusjon: (data && data.praksisstedNavn) || '',
      },
    ),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    tilbake: formatMessage({ id: translations.toPracticeplaces.id }),
    lagret: formatMessage({ id: translations.lagret.id }),
  };

  const savePraksisstedbrukerprofil = (
    fornavn: string,
    etternavn: string,
    epost: string,
    telefonnummer: string,
    omMeg: string,
  ) => {
    praksisstedApi
      .praksisstedbrukerprofilIdPut({
        pathParams: { id: id as string },
        praksisstedbrukerprofilPutDto: { omMeg },
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: formatMessage({ id: translations.lagret.id }),
        });
        reload();
      })
      .catch((err: HttpError) =>
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        }),
      );
  };

  if (!data || !id) {
    return <Spinner />;
  }

  const navn = `${data.bruker.fornavn} ${data.bruker.etternavn}`;

  return (
    <PageLayout
      isIndented
      title={labels.title}
      subtitle={labels.subtitle}
      actionItems={
        <BackButton title={labels.tilbake} path={'/practiceplaces'} />
      }
    >
      <PraksisstedUserprofile
        brukerprofil={{
          ...data,
          kanRedigere:
            data.kanRedigere === PraksisstedbrukerprofilKanRedigereDto.Profil ||
            data.kanRedigere ===
              PraksisstedbrukerprofilKanRedigereDto.ProfilOgTilganger,
        }}
        saveHandler={savePraksisstedbrukerprofil}
        editableFields={['omMeg']}
      />
      <Tilganger
        brukerprofil={data!}
        canAdminister={canAdministerTilganger}
        reloadBrukerprofil={reload}
      />
      <Activities
        userProfileId={id}
        userName={navn}
        isSelf={false}
        isPraksisstedBruker
      />
    </PageLayout>
  );
};

export default PraksisstedbrukerprofilPage;
